import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import Collapse from '@material-ui/core/Collapse';
import classNames from 'classnames';
import LinkedMenuItem from './LinkedMenuItem';
import MenuItemIcon from './MenuItemIcon';
import MenuItemText from './MenuItemText';

const styles = (theme) => ({
  root: {
    '&:hover': {
      backgroundColor: theme.palette.xpl.dm.background,
      '& svg, & span': {
        color: theme.palette.xpl.foreground,
      },
    },
    '&$selected:hover': {
      backgroundColor: theme.palette.xpl.dm.background,
      '& svg:first-child': {
        color: theme.palette.xpl.dm.primary.default,
      },
    },
    '&$selected': {
      backgroundColor: theme.palette.xpl.navigation,
    },
  },
  nested: {
    paddingLeft: '51px',
    borderLeft: 'none',
  },
  itemText: {
    paddingLeft: '5px',
    fontSize: '14px',
  },
  icon: {
    color: theme.palette.xpl.text.secondary,
    marginRight: theme.spacing(1),
    minWidth: 0,
    fontSize: '24px',
  },
  iconSelected: {
    color: theme.palette.xpl.foreground,
  },
  selected: {
    backgroundColor: theme.palette.xpl.navigation,
    color: theme.palette.xpl.foreground,
  },
  subMenuRoot: {
    '&$subMenuSelected': {
      backgroundColor: theme.palette.xpl.gray[800],
      borderLeft: 'none',
      '&:hover': {
        backgroundColor: theme.palette.xpl.dm.background2,
        '& span': {
          color: theme.palette.xpl.dm.primary.default,
        },
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.xpl.dm.background2,
    },
    '&:hover $subMenuItemText': {
      color: theme.palette.xpl.foreground,
    },
  },
  subMenuSelected: {},
  subMenuItemText: {
    paddingLeft: 0,
  },
  subMenuContainer: {
    backgroundColor: theme.palette.xpl.gray[800],
  },
});

const CollapsibleMenuItem = (props) => {
  const {
    onCollapsibleMenuClick,
    name,
    classes,
    subMenuItems,
    className,
    expand,
    selected,
    Icon,
  } = props;

  const handleMenuClick = useCallback(() => {
    onCollapsibleMenuClick(name);
  }, [onCollapsibleMenuClick, name]);

  return (
    <div>
      <ListItem
        button
        onClick={handleMenuClick}
        className={classNames(classes.root, className)}
        selected={selected}
        classes={{ selected: classes.selected }}>
        {Icon && <MenuItemIcon selected={selected} Icon={Icon} />}
        <MenuItemText name={name} selected={selected} isSubMenuItem={false} />
        {expand ? (
          <ExpandLess
            className={classNames(
              classes.icon,
              selected && classes.iconSelected
            )}
          />
        ) : (
          <ExpandMore
            className={classNames(
              classes.icon,
              selected && classes.iconSelected
            )}
          />
        )}
      </ListItem>
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          className={classes.subMenuContainer}>
          {subMenuItems.map((item) => (
            <LinkedMenuItem
              key={item.name}
              name={item.name}
              className={classes.nested}
              isExternal={item.isExternal}
              path={item.path}
              Icon={item.icon}
              selected={item.selected}
              classes={{
                root: classes.subMenuRoot,
                selected: classes.subMenuSelected,
                itemText: classes.subMenuItemText,
              }}
              isSubMenuItem={true}
            />
          ))}
        </List>
      </Collapse>
    </div>
  );
};

CollapsibleMenuItem.propTypes = {
  classes: PropTypes.object.isRequired,
  Icon: PropTypes.object,
  subMenuItems: PropTypes.array,
  name: PropTypes.string,
  onCollapsibleMenuClick: PropTypes.func,
  selected: PropTypes.bool,
  expand: PropTypes.bool,
};

CollapsibleMenuItem.defaultProps = {
  selected: false,
  expand: false,
  Icon: null,
};

export default withStyles(styles)(CollapsibleMenuItem);
